import { Box, Button, Divider, Skeleton, Typography } from '@mui/material'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import JournalLines from './JournalLines'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { IJournal } from '../../interface/IJournal'
import { IUser } from '../../interface/IUser'
import { format } from 'date-fns'
import { de } from 'date-fns/locale/de'
import { enUS } from 'date-fns/locale/en-US'
import axios, { AxiosRequestConfig } from 'axios'
import { useMsal } from '@azure/msal-react'
import { setAlert } from '../../source/slice/AlertSlice'
import { ICustomSnackbar } from '../../interface/ICustomSnackbar'
import { SilentRequest } from '@azure/msal-browser'
import { updateJournal } from '../../utils/functions/updateJournal'
import { setLoadingDate, setRequireCalendarReload } from '../../source/slice/LoadingDateSlice'
import { StatusChip } from '../../utils/StatusChip'
import React, { useEffect, useState } from 'react'
import { SpainAbsenceWarning } from '../Warnings/SpainAbsenceWarning'
import { ICalendar } from '@/interface/ICalendar'

const JournalHeader = () => {
    const { t, i18n } = useTranslation()
    const journal: IJournal = useSelector((state: any) => state.journal?.value)
    const user: IUser = useSelector((state: any) => state.user?.value)
    const loadingDate: boolean = useSelector((state: any) => state.loadingDate?.value)
    const { instance, accounts } = useMsal()
    const dispatch = useDispatch()
    const [absenceWarningOpen, setAbsenceWarningOpen] = useState<boolean>(false);
    const calendarDates: ICalendar[] = useSelector((state: any) => state.calendarDates?.value)
    const currentCalendarDate = calendarDates?.find((date) => {
        const journalDate = journal?.header?.date
        if (!journalDate) return false
        const today = new Date(journalDate)
        const calendarDate = new Date(date.date)
        return calendarDate.getFullYear() === today.getFullYear() && calendarDate.getMonth() === today.getMonth() && calendarDate.getDate() === today.getDate()
    })
    const spainAbsence = user?.worker?.mserp_employmentcategory === 'ES_INT' && currentCalendarDate?.status === 'ABSENCE'
    useEffect(() => {
        if (spainAbsence) {
            setAbsenceWarningOpen(true)
        }
    }, [journal?.header?.date])
    

    const formatDate = () => {
        return i18n.language === 'de' ? (
            format(new Date(journal?.header?.date ?? new Date().toISOString()), 'EEEE, dd.MM.yyyy', {
                locale: de,
            })
        ) : (
            format(new Date(journal?.header?.date ?? new Date().toISOString()), 'EEEE, MM/dd/yyyy', {
                locale: enUS,
            })
        )
    }

    const customSnackbarMessageSuccess: ICustomSnackbar = {
        message: t('zdeheader.deleteSuccess'),
        type: 'success',
    }
    
    const customSnackbarMessageError: ICustomSnackbar = {
        message: t('zdeheader.deleteError'),
        type: 'error',
    }

    const scope : string = process.env.REACT_APP_SCOPE || ''
    const accessTokenRequest : SilentRequest = {
        scopes: [scope],
        account: accounts[0],
        forceRefresh: true
    }

    const deleteJournal = () => {
        instance
            .acquireTokenSilent(accessTokenRequest)
            .then((accessTokenResponse) => {
                const accessToken: string = accessTokenResponse.accessToken
                const config: AxiosRequestConfig = {
                    headers: {
                        'Access-Control-Allow-Origin': '*',
                        accept: 'application/json',
                        'Content-Type': 'application/json',
                        Authorization: 'Bearer ' + accessToken,
                    },
                    params: {
                        date: journal?.header?.date ? format(journal.header.date, 'yyyy-MM-dd') : '',
                    },
                }
                dispatch(setLoadingDate(true))
                axios
                    .delete(`${process.env.REACT_APP_BACKEND_URL}/Journal`, config)
                    .then((response) => {
                        if (response.status === 200) {
                            dispatch(setAlert(customSnackbarMessageSuccess))
                            if (journal.header?.date) {
                                updateJournal(journal.header?.date, instance, accessTokenRequest, dispatch, setLoadingDate)
                            } else {
                                dispatch(setLoadingDate(false))
                            }
                            dispatch(setRequireCalendarReload(true))
                        } else {
                            dispatch(setAlert(customSnackbarMessageError))
                            dispatch(setLoadingDate(false))
                        }
                        console.log(response)
                    })
                    .catch((error) => {
                        console.log(error)
                        dispatch(setLoadingDate(false))
                    })
            })
            .catch((error) => {
                console.log(error)
                dispatch(setAlert(customSnackbarMessageError))
                dispatch(setLoadingDate(false))
            })
    }

    return (
        <>
            <SpainAbsenceWarning isOpen={absenceWarningOpen} onConfirm={() => setAbsenceWarningOpen(false)} />
            <Box display="flex" flexDirection="column" alignItems="start" gap="16px" justifyContent="space-between" width="100%" sx={{ '@media (min-width: 700px)': { flexDirection: 'row', alignItems: 'end', gap: '0' } }}>
                {!loadingDate && user ? (
                    <Typography variant="h4">
                        {formatDate()} | ({user?.resource?.mserp_resourceid}) <StatusChip status={journal?.header?.status ?? ''} />
                    </Typography>
                ) : (
                    <Typography variant="h4">
                        <Skeleton variant="text" sx={{ width: { xs: 200, md: 300 }, display: 'inline-block' }} />
                    </Typography>
                )}
                <Button variant="outlined" onClick={deleteJournal} startIcon={<RestartAltIcon />} color="error" disabled={journal?.header?.mserp_posted}>
                    {t('zdeheader.resetDay')}
                </Button>
            </Box>
            <Divider sx={{ marginTop: '16px' }} />
            <JournalLines />
        </>
    )
}

export default JournalHeader
